import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  adminStats,
  adminListNewsletter,
  adminListContact,
  getAdminToken,
  clearAdminToken,
} from "@/lib/api";
import { Container } from "@/components/Sections";
import { LogOut, Users, Mail, RefreshCw } from "lucide-react";

function StatCard({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-[#ECE4D2] border border-[#1F2A1B]/10 p-8"
    >
      <p className="overline mb-3">{label}</p>
      <p className="font-serif text-5xl md:text-6xl text-[#1F2A1B]">{value}</p>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("newsletter");
  const [stats, setStats] = useState(null);
  const [newsletter, setNewsletter] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const hasToken = Boolean(getAdminToken());

  const handleAuthError = (err) => {
    if (err?.response?.status === 401) {
      clearAdminToken();
      toast.error("Session expired");
      navigate("/admin/login", { replace: true });
      return true;
    }
    return false;
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, n, c] = await Promise.all([
        adminStats(),
        adminListNewsletter(),
        adminListContact(),
      ]);
      setStats(s);
      setNewsletter(n);
      setContacts(c);
    } catch (err) {
      if (!handleAuthError(err)) {
        toast.error("Could not load dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasToken) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasToken]);

  if (!hasToken) {
    return <Navigate to="/admin/login" replace />;
  }

  const onLogout = () => {
    clearAdminToken();
    toast.success("Signed out");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div data-testid="page-admin" className="bg-[#F6F1E6] min-h-screen">
      <section className="pt-32 md:pt-40 pb-16 border-b border-[#1F2A1B]/10">
        <Container>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <p className="overline mb-4">Admin Dashboard</p>
              <h1
                data-testid="admin-title"
                className="font-serif text-4xl md:text-6xl text-[#1F2A1B] leading-[1.02]"
              >
                Newsletter &{" "}
                <span className="italic text-[#3F5132]">enquiries.</span>
              </h1>
            </div>
            <div className="flex gap-3">
              <button
                onClick={fetchAll}
                data-testid="admin-refresh"
                className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase border border-[#1F2A1B]/30 text-[#1F2A1B] px-5 py-3 hover:border-[#C45A3C] hover:text-[#C45A3C] transition-colors"
              >
                <RefreshCw size={14} />
                Refresh
              </button>
              <button
                onClick={onLogout}
                data-testid="admin-logout"
                className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase border border-[#C45A3C] text-[#C45A3C] px-5 py-3 hover:bg-[#C45A3C] hover:text-[#1F2A1B] transition-colors"
              >
                <LogOut size={14} />
                Sign out
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1F2A1B]/10">
            <StatCard
              label="Newsletter subscribers"
              value={stats?.newsletter_subscribers ?? "—"}
              testid="stat-newsletter"
            />
            <StatCard
              label="Contact submissions"
              value={stats?.contact_submissions ?? "—"}
              testid="stat-contact"
            />
          </div>
        </Container>
      </section>

      <section className="py-12">
        <Container>
          <div className="flex border-b border-[#1F2A1B]/10 mb-10">
            <button
              onClick={() => setTab("newsletter")}
              data-testid="tab-newsletter"
              className={`px-6 py-4 text-xs tracking-[0.22em] uppercase font-semibold transition-colors flex items-center gap-2 ${
                tab === "newsletter"
                  ? "text-[#1F2A1B] border-b-2 border-[#C45A3C]"
                  : "text-[#8A8775] hover:text-[#1F2A1B]"
              }`}
            >
              <Users size={14} />
              Newsletter ({newsletter.length})
            </button>
            <button
              onClick={() => setTab("contact")}
              data-testid="tab-contact"
              className={`px-6 py-4 text-xs tracking-[0.22em] uppercase font-semibold transition-colors flex items-center gap-2 ${
                tab === "contact"
                  ? "text-[#1F2A1B] border-b-2 border-[#C45A3C]"
                  : "text-[#8A8775] hover:text-[#1F2A1B]"
              }`}
            >
              <Mail size={14} />
              Contact ({contacts.length})
            </button>
          </div>

          {loading && (
            <p className="text-[#8A8775]" data-testid="admin-loading">
              Loading…
            </p>
          )}

          {!loading && tab === "newsletter" && (
            <div data-testid="newsletter-list" className="border border-[#1F2A1B]/10">
              <div className="grid grid-cols-12 px-6 py-4 border-b border-[#1F2A1B]/10 text-xs tracking-[0.22em] uppercase text-[#8A8775]">
                <div className="col-span-5">Email</div>
                <div className="col-span-4">Organisation</div>
                <div className="col-span-3">Subscribed</div>
              </div>
              {newsletter.length === 0 ? (
                <p className="px-6 py-10 text-[#8A8775]">No subscribers yet.</p>
              ) : (
                newsletter.map((n) => (
                  <div
                    key={n.id}
                    data-testid={`newsletter-row-${n.id}`}
                    className="grid grid-cols-12 px-6 py-4 border-b border-[#1F2A1B]/10 hover:bg-[#ECE4D2] transition-colors"
                  >
                    <div className="col-span-5 text-[#1F2A1B] truncate">
                      {n.email}
                    </div>
                    <div className="col-span-4 text-[#4F5A48] truncate">
                      {n.organisation || "—"}
                    </div>
                    <div className="col-span-3 text-[#8A8775] text-sm">
                      {formatDate(n.created_at)}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {!loading && tab === "contact" && (
            <div data-testid="contact-list" className="border border-[#1F2A1B]/10">
              <div className="grid grid-cols-12 px-6 py-4 border-b border-[#1F2A1B]/10 text-xs tracking-[0.22em] uppercase text-[#8A8775]">
                <div className="col-span-3">Name</div>
                <div className="col-span-3">Email</div>
                <div className="col-span-3">Interest</div>
                <div className="col-span-3">Received</div>
              </div>
              {contacts.length === 0 ? (
                <p className="px-6 py-10 text-[#8A8775]">No enquiries yet.</p>
              ) : (
                contacts.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelectedContact(c)}
                    data-testid={`contact-row-${c.id}`}
                    className="grid grid-cols-12 px-6 py-4 border-b border-[#1F2A1B]/10 hover:bg-[#ECE4D2] transition-colors text-left w-full"
                  >
                    <div className="col-span-3 text-[#1F2A1B] truncate">
                      {c.name}
                    </div>
                    <div className="col-span-3 text-[#4F5A48] truncate">
                      {c.email}
                    </div>
                    <div className="col-span-3 text-[#4F5A48] truncate">
                      {c.interest || "—"}
                    </div>
                    <div className="col-span-3 text-[#8A8775] text-sm">
                      {formatDate(c.created_at)}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </Container>
      </section>

      {selectedContact && (
        <div
          data-testid="contact-detail-overlay"
          className="fixed inset-0 z-[60] bg-[#F6F1E6]/90 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setSelectedContact(null)}
        >
          <div
            className="bg-[#ECE4D2] border border-[#1F2A1B]/15 max-w-2xl w-full p-10 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="overline mb-3">Enquiry</p>
            <h3 className="font-serif text-3xl text-[#1F2A1B] mb-2">
              {selectedContact.name}
            </h3>
            <p className="text-[#4F5A48] mb-6">
              {selectedContact.email}
              {selectedContact.organisation && ` · ${selectedContact.organisation}`}
              {selectedContact.role && ` · ${selectedContact.role}`}
            </p>
            {selectedContact.interest && (
              <p className="text-xs tracking-[0.22em] uppercase text-[#3F5132] border border-[#3F5132]/40 inline-block px-3 py-2 mb-6">
                {selectedContact.interest}
              </p>
            )}
            <p className="text-[#1F2A1B] text-base leading-relaxed whitespace-pre-wrap border-l-2 border-[#C45A3C] pl-5 mb-8">
              {selectedContact.message}
            </p>
            <p className="text-[#8A8775] text-xs mb-6">
              Received {formatDate(selectedContact.created_at)}
            </p>
            <button
              onClick={() => setSelectedContact(null)}
              data-testid="contact-detail-close"
              className="btn-ghost"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
